import React, { useMemo } from 'react';
import type { Logger } from '@feature-hub/logger';
import type {
  ContentService,
  GfaLocaleService,
  I18NService,
  OneGraphService,
  RenderModeService,
  TrackingService,
} from '../feature-service-types';

export interface FeatureAppContextState {
  readonly featureAppId: string;
  readonly logger: Logger;
  readonly trackingService: TrackingService | undefined;
  readonly contentService: ContentService;
  readonly renderModeService: RenderModeService;
  readonly localeService: GfaLocaleService;
  readonly i18nService: I18NService;
  readonly oneGraphService: OneGraphService;
}

export const FeatureAppContext = React.createContext<FeatureAppContextState>(
  {} as FeatureAppContextState,
);
export interface FeatureAppContextProviderProps {
  readonly featureAppId: string;
  readonly logger: Logger;
  readonly trackingService: TrackingService | undefined;
  readonly contentService: ContentService;
  readonly renderModeService: RenderModeService;
  readonly localeService: GfaLocaleService;
  readonly i18nService: I18NService;
  readonly oneGraphService: OneGraphService;
}

export const FeatureAppContextProvider = ({
  logger,
  children,
  trackingService,
  contentService,
  renderModeService,
  localeService,
  i18nService,
  oneGraphService,
  featureAppId,
}: React.PropsWithChildren<FeatureAppContextProviderProps>): JSX.Element => {
  const contextValue = useMemo(() => {
    return {
      logger,
      trackingService,
      contentService,
      renderModeService,
      localeService,
      i18nService,
      oneGraphService,
      featureAppId,
    };
  }, []);
  return <FeatureAppContext.Provider value={contextValue}>{children}</FeatureAppContext.Provider>;
};

function getFeatureAppContext() {
  const context = React.useContext(FeatureAppContext);
  if (context === null) {
    throw Error('Did you forget to wrap your Feature App in <FeatureAppContextProvider>?');
  }
  return context;
}

export function useLogger(): Logger {
  return getFeatureAppContext().logger;
}
